import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

import { BACKEND_URL, API } from '../utils/apiConfig';

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchCurrentAdmin();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchCurrentAdmin = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setAdmin(response.data);
    } catch (error) {
      console.error('Failed to fetch admin:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, admin } = response.data;
      setToken(access_token);
      setAdmin(admin);
      localStorage.setItem('token', access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return { success: true, admin };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Login failed' };
    }
  };

  const register = async (email, password, name) => {
    try {
      await axios.post(`${API}/auth/register`, { email, password, name });
      return await login(email, password);
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Registration failed' };
    }
  };

  const logout = () => {
    setToken(null);
    setAdmin(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
  };

  const isStaff = admin?.role === 'staff';
  const isEventAdmin = admin?.role === 'event_admin';

  return (
    <AuthContext.Provider value={{ admin, token, login, register, logout, loading, isStaff, isEventAdmin }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};